<template>
  <div class="fd" ref="raiz">
    <button
      type="button"
      class="fd__gatilho"
      :class="{ 'fd__gatilho--filtrando': value.length }"
      @click="toggle"
    >
      <fa-icon icon="filter" />
      <span class="fd__rotulo">{{ rotulo }}</span>
      {{ resumo }}
      <fa-icon icon="chevron-down" class="fd__chevron" :class="{ 'fd__chevron--aberto': aberto }" />
    </button>

    <div
      v-show="aberto"
      class="fd__painel"
      ref="painel"
      :style="[{ width: largura + 'px' }, estiloPainel]"
    >
      <div class="fd__acoes">
        <span class="fd__contagem">{{ cabecalho }}</span>
        <span>
          <button type="button" class="fd__link" @click="marcarTodos">Marcar todos</button>
          <button type="button" class="fd__link" :disabled="!pendentes.length" @click="limpar">
            Limpar
          </button>
        </span>
      </div>

      <ul class="fd__lista">
        <li v-for="opcao in opcoes" :key="opcao.valor">
          <label class="fd__item" :class="{ 'fd__item--marcado': pendentes.includes(opcao.valor) }">
            <input type="checkbox" :value="opcao.valor" v-model="pendentes" />
            <span class="fd__dot" :style="{ background: opcao.cor }"></span>
            <span class="fd__nome" :class="{ 'fd__nome--mono': mono }">{{ opcao.rotulo }}</span>
            <span v-if="opcao.total != null" class="fd__total">{{ opcao.total }}</span>
          </label>
        </li>
      </ul>

      <!-- Só aparece quando há mudança a aplicar: desmarcar de volta some com ele. -->
      <div v-if="temPendencia" class="fd__rodape">
        <span class="fd__pendente">
          {{ pendentes.length ? pendentes.length + ' selecionado(s)' : 'Nenhum' }}
        </span>
        <CButton color="primary" size="sm" @click="aplicar">APLICAR</CButton>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Filtro em dropdown com checkboxes e aplicação explícita.
 *
 * A marcação fica PENDENTE dentro do painel: só vira filtro aplicado (emitindo
 * `input`) quando o usuário clica em APLICAR ou dispensa o painel — clique fora
 * ou no próprio gatilho. Esc fecha descartando a pendência.
 *
 * Usado pelos filtros de Tipo e de Status da tela de Logs de Integração.
 */
import dropdownPosicao from './dropdownPosicao';

export default {
  name: 'FiltroDropdown',
  mixins: [dropdownPosicao],
  props: {
    // Valores aplicados (v-model). Nunca é mutado aqui.
    value: { type: Array, default: () => [] },
    // [{ valor, rotulo, cor, total? }]
    opcoes: { type: Array, default: () => [] },
    rotulo: { type: String, default: '' },
    cabecalho: { type: String, default: '' },
    // Texto do gatilho quando nada está selecionado.
    rotuloVazio: { type: String, default: 'Todos' },
    // Nomes em fonte monoespaçada (usado pelos tipos de log).
    mono: { type: Boolean, default: false },
    largura: { type: Number, default: 340 },
  },
  data() {
    return {
      aberto: false,
      pendentes: [],
    };
  },
  computed: {
    resumo() {
      return this.value.length ? `${this.value.length} selecionado(s)` : this.rotuloVazio;
    },
    temPendencia() {
      const aplicados = [...this.value].sort().join('|');
      const pendentes = [...this.pendentes].sort().join('|');

      return aplicados !== pendentes;
    },
  },
  methods: {
    abrir() {
      // Parte sempre do filtro aplicado, descartando pendências antigas.
      this.pendentes = [...this.value];
      this.aberto = true;

      // No nextTick para o próprio clique que abriu não ser lido como "fora".
      this.$nextTick(() => {
        // Painel já renderizado: agora dá para medir o espaço livre.
        this.observarPosicao();
        document.addEventListener('click', this.onClickFora, true);
        document.addEventListener('keydown', this.onEsc, true);
      });
    },
    fechar() {
      this.aberto = false;
      this.pararObservarPosicao();
      document.removeEventListener('click', this.onClickFora, true);
      document.removeEventListener('keydown', this.onEsc, true);
    },
    toggle() {
      // Fechar pelo gatilho equivale a dispensar: confirma o que está marcado.
      if (this.aberto) {
        this.confirmar();
        return;
      }

      this.abrir();
    },
    onClickFora(evento) {
      const raiz = this.$refs.raiz;

      if (raiz && !raiz.contains(evento.target)) {
        this.confirmar();
      }
    },
    onEsc(evento) {
      if (evento.key === 'Escape') {
        this.fechar();
      }
    },
    // Fecha aplicando, mas só emite quando algo mudou de fato.
    confirmar() {
      const mudou = this.temPendencia;
      const pendentes = [...this.pendentes];

      this.fechar();

      if (mudou) {
        this.$emit('input', pendentes);
      }
    },
    aplicar() {
      this.confirmar();
    },
    marcarTodos() {
      this.pendentes = this.opcoes.map((opcao) => opcao.valor);
    },
    limpar() {
      this.pendentes = [];
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickFora, true);
    document.removeEventListener('keydown', this.onEsc, true);
  },
};
</script>

<style scoped>
.fd {
  position: relative;
}

.fd__gatilho {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 13px;
  font-size: 12px;
  font-weight: 600;
  color: #2c3441;
  background: #fff;
  border: 1px solid #d4d9df;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}

.fd__gatilho:hover {
  background: #f7f9fb;
}

.fd__gatilho--filtrando {
  border-color: #2b3556;
  color: #2b3556;
}

.fd__rotulo {
  color: #8a929c;
  font-weight: 400;
}

.fd__chevron {
  transition: transform 0.15s ease;
  opacity: 0.6;
}

.fd__chevron--aberto {
  transform: rotate(180deg);
}

.fd__painel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 41;
  max-width: 90vw;
  background: #fff;
  border: 1px solid #d9dee4;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(16, 24, 40, 0.18);
}

.fd__acoes {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #eef1f4;
}

.fd__contagem {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.fd__link {
  margin-left: 10px;
  padding: 0;
  font-size: 11px;
  font-weight: 600;
  color: #3d7fc4;
  background: none;
  border: none;
  cursor: pointer;
}

.fd__link:disabled {
  color: #c3cad2;
  cursor: not-allowed;
}

.fd__lista {
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
}

.fd__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 7px 8px;
  border-radius: 7px;
  cursor: pointer;
}

.fd__item:hover {
  background: #f7f9fb;
}

.fd__item--marcado {
  background: #f2f7fc;
}

.fd__item input {
  flex-shrink: 0;
  cursor: pointer;
}

.fd__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.fd__nome {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: #2c3441;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fd__nome--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.fd__total {
  font-size: 11px;
  color: #9aa2ac;
  flex-shrink: 0;
}

.fd__rodape {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid #eef1f4;
  background: #f7f9fb;
  border-radius: 0 0 10px 10px;
}

.fd__pendente {
  font-size: 11px;
  color: #9aa2ac;
}
</style>
